import { useCallback, useContext, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { openHelpChat, openHelpChatSuccess } from '~/actions/help.actions';
import { getHelpStatus } from '~/selectors/help.selectors';
import { getUserObjectId, getUserZendeskJwt } from '~/selectors/user.selectors';
import { FeatureFlags, useFeatureFlag } from '~/utils/featureflags';
import { requestableIsLoading } from '~/utils/fetchable';

import { ZendeskWidgetContext } from './ZendeskWidgetContext';
import { zendeskSyncUser, zendeskUnbindUser } from './zendeskAuthentication';

const tagId = 'ze-snippet';
const widgetUrl = `https://static.zdassets.com/ekr/snippet.js?key=${CONFIG.zendesk}`;
const helpCenterUrl = 'https://support.goshippo.com/hc/en-us/requests/new';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createTag = (onLoad: any) => {
  const tag = document.createElement('script');
  tag.src = widgetUrl;
  tag.id = tagId;

  tag.onload = onLoad;

  return document.body.appendChild(tag);
};

/**
 * The zendesk widget sets a custom viewport, overriding the
 * one provided in the index.html file.
 * This is messing with the zoom on iOS during login.
 * Since there is no way to disable that behavior, we reset
 * the viewport meta everytime it's changed by the widget.
 */
const restoreViewport = (originalViewport: null | string) => {
  const targetElement = document.querySelector('meta[name="viewport"]');
  if (!originalViewport || !targetElement) return;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'content') return;

      const element = mutation.target as HTMLElement;
      const currentValue = element.getAttribute('content');
      if (currentValue === originalViewport) return;

      element.setAttribute('content', originalViewport);
    });
  });

  observer.observe(targetElement, {
    attributeFilter: ['content'],
    attributeOldValue: true,
    attributes: true,
  });

  return () => observer.disconnect();
};

// This is used exclusively by the context provider. You should not import this hook directly.
export const useProviderHelp = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const helpStatus = useSelector(getHelpStatus);
  const isLoading = requestableIsLoading(helpStatus);
  const zendeskFlag = useFeatureFlag(FeatureFlags.WEBAPP_ZENDESK_ENABLED);
  const isZendeskEnabled = !!zendeskFlag?.enabled;
  const zendeskAuthFlag = useFeatureFlag(FeatureFlags.ZENDESK_AUTHENTICATION_ENABLED);
  const isZendeskAuthEnabled = !!zendeskAuthFlag?.enabled;
  const zendeskJwt = useSelector(getUserZendeskJwt);
  const userObjectId = useSelector(getUserObjectId);

  // Load the Zendesk widget script when the component mounts
  useEffect(() => {
    if (!isZendeskEnabled) {
      // The flag can flip mid-session (Optimizely auto-updates), so close
      // the widget and reset state if it was already loaded
      if (window.zE) window.zE('messenger', 'close');
      setIsOpen(false);
      setIsScriptLoaded(false);
      setUnreadCount(0);
      return;
    }

    const scriptExists = !!document.getElementById(tagId);
    let shouldDispatchSuccess = true;
    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;

    const setupZendeskListeners = () => {
      if (!isMounted) return;

      window.zE('messenger:on', 'open', () => {
        if (shouldDispatchSuccess && isMounted) {
          dispatch(openHelpChatSuccess());
          shouldDispatchSuccess = false;
        }
        if (isMounted) setIsOpen(true);
      });

      window.zE('messenger:on', 'close', () => {
        if (isMounted) setIsOpen(false);
      });

      // Listen for unread message count changes
      window.zE('messenger:on', 'unreadMessages', (count: number) => {
        if (isMounted) setUnreadCount(count);
      });

      // Ensure the widget is always closed on page load
      window.zE('messenger', 'close');
      if (isMounted) setIsScriptLoaded(true);
    };

    if (!scriptExists) {
      dispatch(openHelpChat());
      createTag(setupZendeskListeners);
    } else {
      // Script exists, but we need to wait for window.zE to be available
      shouldDispatchSuccess = false; // Don't dispatch success if script already exists

      const checkZendeskReady = (attempts = 0) => {
        const maxAttempts = 50; // 5 seconds total (50 * 100ms)

        if (!isMounted) return;

        if (window.zE) {
          setupZendeskListeners();
        } else if (attempts < maxAttempts) {
          // Continue polling with exponential backoff for better performance
          const delay = Math.min(100 + attempts * 10, 500); // Start at 100ms, max 500ms
          timeoutId = setTimeout(() => checkZendeskReady(attempts + 1), delay);
        } else {
          // Max attempts reached, silently fail gracefully
          // Zendesk widget failed to load after maximum retry attempts
        }
      };

      checkZendeskReady();
    }

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [dispatch, isZendeskEnabled]);

  useEffect(() => {
    if (!isScriptLoaded) return;
    if (!isZendeskAuthEnabled || !zendeskJwt || !userObjectId) {
      void zendeskUnbindUser();
      return;
    }

    void zendeskSyncUser(zendeskJwt, userObjectId);
  }, [isScriptLoaded, isZendeskAuthEnabled, userObjectId, zendeskJwt]);

  useEffect(
    () =>
      restoreViewport(
        document.querySelector('meta[name="viewport"]')?.getAttribute('content') || null,
      ),
    [],
  );

  const openChat = useCallback(() => {
    // Fall back to the Help Center when the widget is disabled, so
    // consumers offering support actions keep working
    if (!isZendeskEnabled) return window.open(helpCenterUrl, '_blank', 'noopener');

    if (!isScriptLoaded) return;

    // Toggle the widget based on current state
    if (isOpen) return window.zE('messenger', 'close');
    else return window.zE('messenger', 'open');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isOpen, isScriptLoaded, isZendeskEnabled]);

  return { isLoading, isOpen, openChat, unreadCount };
};

// This is what you should use if you want to open the help chat.
const useHelp = () => {
  const { isLoading, isOpen, openChat, unreadCount } = useContext(ZendeskWidgetContext);

  return {
    isLoading,
    isOpen,
    openChat,
    unreadCount,
  };
};

export default useHelp;
